import { FC, useEffect, useState } from "react";
import { Space, Tag, Tooltip, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { useGetOrderInProgress } from "hooks/useFactory";
import { GetOrderInProgress } from "services/interface";
import { formatDateAndTime } from "utils/convertData";
import { numberFormatePrice } from "utils/formateNumber";
import { getOrderStatusLabel, getPaymentConditionStatus } from "utils/orderStatus";
import TableList from "./TableList";
import styled from "@emotion/styled";

interface TableInProgressProps {
  startDate: string;
  endDate: string;
}

const ellipsisStyle: React.CSSProperties = {
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "block",
};

const truncateText = (text: string | undefined) => {
  if (!text) return <Typography.Text>-</Typography.Text>;
  return (
    <Tooltip title={text}>
      <Typography.Text ellipsis style={ellipsisStyle}>
        {text}
      </Typography.Text>
    </Tooltip>
  );
};

const conditionTagStyle: React.CSSProperties = { whiteSpace: "normal", marginInlineEnd: 0 };

const renderCondition = (record: GetOrderInProgress) => {
  const { state, days } = getPaymentConditionStatus(
    record.payment_status,
    record.payment_date,
    record.item_status
  );
  if (state === "met") return <Tag color="green" style={conditionTagStyle}>ผ่านเงื่อนไข</Tag>;
  if (state === "wait_shipped") return <Tag color="red" style={conditionTagStyle}>ไม่ผ่านเงื่อนไข<br />ยืนยันรับสินค้า</Tag>;
  return <Tag color="orange" style={conditionTagStyle}>ไม่ผ่านเงื่อนไขจ่ายเงิน<br />(จ่ายแล้ว {days} วัน)</Tag>;
};

const TableInProgress: FC<TableInProgressProps> = ({ startDate, endDate }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [tableData, setTableData] = useState<GetOrderInProgress[]>([]);

  const { data } = useGetOrderInProgress({
    start_date: startDate,
    end_date: endDate,
    page_number: page,
    row_per_page: pageSize,
    sort_by: "order_code",
    sort_direction: "desc",
  });

  useEffect(() => {
    if (data) setTableData(data.data.data);
  }, [data]);

  const columns: ColumnsType<GetOrderInProgress> = [
    {
      title: "หมายเลขคำสั่งซื้อ",
      dataIndex: "order_code",
      width: 150,
      render: (_text, record) => truncateText(record.order_code),
    },
    {
      title: "เงื่อนไข",
      dataIndex: "payment_status",
      align: "center",
      width: 150,
      render: (_text, record) => renderCondition(record),
    },
    {
      title: "สินค้า",
      dataIndex: "product_name",
      align: "left",
      render: (_text, record) => (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          {truncateText(record.product_name)}
          {record.sku_description && (
            <Typography.Text type="secondary" ellipsis style={ellipsisStyle}>
              <Tooltip title={record.sku_description}>({record.sku_description})</Tooltip>
            </Typography.Text>
          )}
        </Space>
      ),
    },
     {
      title: "จำนวนสินค้า",
      dataIndex: "item_qty",
      width: 90,
      align: "center",
      render: (_text, record) => <Typography.Text>{record.item_qty}</Typography.Text>,
    },
    {
      title: (
        <span style={{ whiteSpace: "normal" }}>
          ราคารวมที่โอนให้
          <br />
          ผู้ประกอบการรวม vat
        </span>
      ),
      dataIndex: "total_base_factory_price",
      width: 150,
      align: "center",
      render: (_text, record) => (
        <Typography.Text>
          {numberFormatePrice(record.total_base_factory_price ?? 0)}
        </Typography.Text>
      ),
    },
    {
      title: "สถานะ",
      dataIndex: "item_status",
      align: "center",
      width: 110,
      render: (_text, record) => (
        <Typography.Text>{getOrderStatusLabel(record.item_status)}</Typography.Text>
      ),
    },
    {
      title: "วันที่สั่งซื้อ",
      dataIndex: "order_date",
      align: "center",
      width: 120,
      render: (_text, record) => (
        <Typography.Text>{formatDateAndTime(record.order_date)}</Typography.Text>
      ),
    },
    {
      title: "วันที่ชำระเงิน",
      dataIndex: "payment_date",
      align: "center",
      width: 120,
      render: (_text, record) => (
        <Typography.Text>
          {record.payment_date ? formatDateAndTime(record.payment_date) : "-"}
        </Typography.Text>
      ),
    },
    // {
    //   title: "จัดการ",
    //   width: 150,
    //   align: "center",
    //   fixed: "right",
    //   render: () => <Space />,
    // },
  ];

  return (
    <CardWrapperTable>
      <TableTitle>
        รายการคำสั่งที่กำลังดำเนินการ ({data?.data.pagination.total || 0})
      </TableTitle>
      <ConditionDescription>
        <ConditionItem color="#52c41a">ถ้าตรงตามเงื่อนไข จ่ายเงินครบ 7 วัน และมีการยืนยันรับสินค้า</ConditionItem>
        <ConditionItem color="#faad14">
          หากมีข้อส่งสัยสามารถติดต่อได้ที่{" "}
          <Typography.Link
            href="mailto:contact@bangbowplatform.com"
            style={{ textDecoration: "underline" }}
          >
            contact@bangbowplatform.com
          </Typography.Link>
        </ConditionItem>
      </ConditionDescription>
      <TableList
        data={tableData}
        columns={columns}
        total={data?.data.pagination.total || 0}
        current={page}
        pageSize={pageSize}
        onPageChange={(p, size) => {
          setPage(p);
          setPageSize(size);
        }}
      />
    </CardWrapperTable>
  );
};

export default TableInProgress;

const CardWrapperTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
  background-color: #ffffff;
  padding: 5px;
`;

const TableTitle = styled.p`
  font-size: 16px;
  font-weight: bold;
  padding-left: 10px;
  padding-top: 10px;
`;

const ConditionDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 10px 10px;
`;

const ConditionItem = styled.p<{ color: string }>`
  font-size: 14px;
  color: #555;
  margin: 0;
  &::before {
    content: "●";
    color: ${({ color }) => color};
    margin-right: 6px;
  }
`;
